import { ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { UploadStatus, type UploadFileData } from '@/types/aeroUpload/aeroUpload'
import { useFileUploadApi } from '@/api/dup/fileUpload/fileUploadApi'
import { useMiiaFwStore } from './miiaFwStore'
import { useDisasterStore } from './disasterStore'
import MiiaFwConfig from '@/configs/config'
import { useRunFileProcessorEcsApi } from '@/api/dup/runFileProcessorEcs/runFileProcessorEcs'
import { DupCategoryId } from '@/types/api/dup/runFileProcessorEcs/runFileProcessorEcs'
import { parallelChunksUpload } from '@/composables/common/parallelChunksUpload/parallelChunksUpload'

export const useAeroUploadStore = defineStore('aeroUploadStore', () => {
  const miiaFwStore = useMiiaFwStore()
  const { govCode } = storeToRefs(miiaFwStore)
  const disasterStore = useDisasterStore()
  const { disasterId } = storeToRefs(disasterStore)

  const {
    createMultipartUpload,
    getPresignedUrl,
    uploadChunkWithProgress,
    cancelChunkUpload,
    completeMultipartUpload
  } = useFileUploadApi()
  const { runEcs } = useRunFileProcessorEcsApi()

  /** アップロード中フラグ(true: アップロード中, false: それ以外) */
  const isUploading = ref<boolean>(false)
  /** アップロード対象データ */
  const uploadFileList = ref<UploadFileData[]>([])

  /** ファイル識別用のキーを生成する */
  const generateFileKey = (): string => {
    const timestamp = Date.now().toString(36)
    const randomStr = Math.random().toString(36).substring(2, 7)
    return `${timestamp}-${randomStr}`
  }

  /**
   * アップロード対象に追加する。
   * @param files 対象データ
   */
  const addFiles = (...files: File[]) => {
    if (isUploading.value) {
      return
    }
    uploadFileList.value.push(
      ...files.map(file => ({
        key: generateFileKey(),
        fileName: file.name,
        file: file,
        status: UploadStatus.WAIT,
        progress: 0
      }))
    )
  }

  /** アップロード対象をリセットする。 */
  const clearFiles = () => {
    if (isUploading.value) {
      return
    }
    uploadFileList.value.splice(0)
  }

  /** アップロードを中断する */
  const abortUpload = () => {
    if (!isUploading.value) {
      return
    }
    uploadFileList.value
      .filter(data => data.status !== UploadStatus.COMPLETE)
      .forEach(data => {
        cancelChunkUpload(data.key)
      })
    uploadFileList.value = uploadFileList.value.filter(
      data => data.status === UploadStatus.COMPLETE
    )

    isUploading.value = false
  }

  /** ファイルの一括アップロードを実行する。 */
  const executeUpload = async () => {
    const fileDataList = uploadFileList.value.filter(data => data.status !== UploadStatus.COMPLETE)
    if (fileDataList.length === 0 || isUploading.value) {
      return
    }

    isUploading.value = true
    try {
      fileDataList.forEach(data => {
        data.progress = 0
        data.status = UploadStatus.WAITING_UPLOAD
        data.completedDateTime = undefined
      })

      const CONCURRENT_LIMIT = 3
      let currentIndex = 0

      // 実際のアップロード処理を行う関数
      const uploadNext = async (): Promise<void> => {
        if (currentIndex >= fileDataList.length) return // 全ての処理が割り当てられたら終了

        const fileData = fileDataList[currentIndex]
        currentIndex++
        fileData.status = UploadStatus.UPLOADING

        try {
          await executeMultiPartUpload(fileData)
          fileData.progress = 100
          fileData.status = UploadStatus.COMPLETE
          fileData.completedDateTime = new Date()
        } catch (e) {
          console.error(e)
          fileData.status = UploadStatus.ERROR
        }

        await uploadNext()
      }

      // 最初にCONCURRENT_LIMIT個の処理を同時に開始する
      await Promise.all(
        Array(CONCURRENT_LIMIT)
          .fill(null)
          .map(() => uploadNext())
      )

      return !fileDataList.some(e => e.status === UploadStatus.ERROR)
    } finally {
      isUploading.value = false
    }
  }

  /**
   * マルチパートアップロードを実行する
   * @param fileData アップロード対象のファイルデータ
   */
  const executeMultiPartUpload = async (fileData: UploadFileData) => {
    const commonParam = {
      system: MiiaFwConfig.config.sysId,
      govCode: govCode.value!,
      disasterId: disasterId.value!,
      dupCategoryId: DupCategoryId.AERIAL_PIC
    } as const

    const { key: uploadKey, uploadId } = await createMultipartUpload({
      ...commonParam,
      fileName: fileData.fileName
    })

    const multipartList = await parallelChunksUpload(
      fileData,
      commonParam,
      uploadKey,
      uploadId,
      getPresignedUrl,
      uploadChunkWithProgress
    )

    // マルチパートアップロード完了通知
    await completeMultipartUpload({
      ...commonParam,
      key: uploadKey,
      uploadId,
      multipartList
    })

    // ECS実行
    await runEcs({ ...commonParam, key: uploadKey })

    return fileData
  }

  /** ストアの状態をリセットする */
  const reset = () => {
    isUploading.value = false
    uploadFileList.value.splice(0)
  }

  return { isUploading, uploadFileList, addFiles, clearFiles, executeUpload, abortUpload, reset }
})
